import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useDeletePropertyTypeMutation } from '@/hooks/queries/usePropertiesTemplates';
import type { ApiPropertyType } from '@/types/api';

interface DeleteConfirmationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  propertyType: ApiPropertyType | null;
}

export function DeleteConfirmationDialog({
  open,
  onOpenChange,
  propertyType,
}: DeleteConfirmationDialogProps) {
  const { t, i18n } = useTranslation();
  const isRTL = useMemo(() => i18n.language === 'ar', [i18n.language]);

  const deleteMutation = useDeletePropertyTypeMutation({
    onSuccessCallback: () => {
      onOpenChange(false);
    },
  });

  const handleConfirm = () => {
    if (propertyType) {
      deleteMutation.mutate(propertyType.id);
    }
  };

  if (!propertyType) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={cn('max-w-md', isRTL && 'rtl')}>
        <DialogHeader className={cn(isRTL && 'text-right')}>
          <div
            className={cn(
              'flex items-center gap-3',
              isRTL ? 'flex-row-reverse' : 'flex-row'
            )}
          >
            <div className="p-2 rounded-full bg-destructive/10">
              <AlertTriangle className="w-6 h-6 text-destructive" />
            </div>
            <DialogTitle className="text-xl">
              {t(
                'templates.admin.deleteConfirm.title',
                'Delete Property Type?'
              )}
            </DialogTitle>
          </div>
          <DialogDescription className={cn('pt-4', isRTL && 'text-right')}>
            {t(
              'templates.admin.deleteConfirm.message',
              'Are you sure you want to delete'
            )}
            <span className="font-semibold text-foreground">
              {' '}
              "{propertyType.name}"{' '}
            </span>
            {t(
              'templates.admin.deleteConfirm.warning',
              '? This action cannot be undone.'
            )}
          </DialogDescription>
        </DialogHeader>

        <DialogFooter className={cn('gap-2 pt-4', isRTL && 'flex-row-reverse')}>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={deleteMutation.isPending}
          >
            {t('templates.admin.deleteConfirm.cancel', 'Cancel')}
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleConfirm}
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending
              ? t('templates.admin.deleteConfirm.deleting', 'Deleting...')
              : t('templates.admin.deleteConfirm.delete', 'Delete')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
